import React, { useEffect, useState, useMemo } from 'react';
import { array, bool, func, number, object, string, oneOfType } from 'prop-types';
import { useDispatch } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import toString from 'lodash/toString';
import orderBy from 'lodash/orderBy';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import Placeholder from '../../Placeholder/Placeholder';
import {
  getGenericSurveyRow,
  getTransformedColumns
} from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import { SLIDEOUTS } from '../../../Slideout/Slideouts';
import './MarketplaceMatchesGrid.scss';
import classnames from 'classnames';
import GG_DEFAULTS from '../../../../lib/genericGridDefaults';
import { jobMatchGridActions } from '../../LandingPage/JobMatchDetail/JobMatchGridCreate';

// UI custom filter: Filter by purchased/unpurchased, by search term, and preferences
function filterPurchasedAndSearch(matches, showPurchased, filterText, data, terms, settings) {
  const surveyJobs = showPurchased ? matches?.purchased : matches?.unpurchased;

  if (!surveyJobs || surveyJobs?.length === 0) {
    return [];
  }

  let newData = cloneDeep(data);
  const termsArray = terms.map(e => e.term);
  const purchasedIndex = termsArray.indexOf('purchased');
  const industryIndex = termsArray.indexOf('surveyIndustry');
  const geographyIndex = termsArray.indexOf('surveyGeography');
  const employeeCountIndex = termsArray.indexOf('employeeCount');
  const companyCountIndex = termsArray.indexOf('companyCount');
  const confidenceIndex = termsArray.indexOf('confidence');

  // only filter on these terms when searching with input
  const validSearchIndexes = [
    termsArray.indexOf('surveyCode'),
    termsArray.indexOf('surveyCurrency'),
    termsArray.indexOf('surveyJobCode'),
    termsArray.indexOf('surveyTitle'),
    termsArray.indexOf('surveyJobTitle'),
    termsArray.indexOf('jobTitle')
  ];

  if (data) {
    const lower = toString(filterText).toLowerCase();

    newData = data.filter(row => {
      let match = false;
      for (let i = 0; i < row.length; i++) {
        if (validSearchIndexes.includes(i)) {
          const thisLower = toString(row[i]).toLowerCase();

          if (thisLower.includes(lower)) {
            match = true;
          }
        }
      }

      if (!match) {
        return false;
      }

      let shouldReturn = true;

      // filter on saved preferences from slideout
      if (settings) {
        const industriesList = settings.industries?.map(i => i.label?.toLowerCase() || '') || [];
        const geographiesList = settings.geographies?.map(i => i.label?.toLowerCase() || '') || [];

        if ((row[purchasedIndex] || false) !== showPurchased) {
          shouldReturn = false;
        } else if (settings.minEmployee && row[employeeCountIndex] < settings.minEmployee) {
          shouldReturn = false;
        } else if (settings.minCompany && row[companyCountIndex] < settings.minCompany) {
          shouldReturn = false;
        } else if (industriesList.length > 0 && !industriesList.includes(row[industryIndex]?.toLowerCase())) {
          shouldReturn = false;
        } else if (geographiesList.length > 0 && !geographiesList.includes(row[geographyIndex]?.toLowerCase())) {
          shouldReturn = false;
        } else if (settings.onlyShowFrequent && row[confidenceIndex] < 0.3) {
          shouldReturn = false;
        }
      }

      return shouldReturn;
    });
  }

  // Sort final result by confidence in descending order
  newData = orderBy(newData, [o => o[confidenceIndex]], ['desc']);

  return newData || [];
}

// combine grid data result and survey job recommendations
function combineData(matches, grid, showPurchasedSurveys) {
  const newGrid = cloneDeep(grid);
  const surveyJobs = showPurchasedSurveys ? matches?.purchased : matches?.unpurchased;

  // If there are no results for selected tab, then skip combination logic below
  if (!surveyJobs || surveyJobs.length === 0 || !grid) {
    return newGrid;
  }

  let count = grid?.terms?.length;

  if (!showPurchasedSurveys) {
    newGrid.terms = [];
    count = 0;
  }

  // Create terms from recommender results and combine with grid terms
  surveyJobs?.forEach(surveyJob => {
    forEach(surveyJob, (value, key) => {
      const check = newGrid?.terms?.filter(t => t.term === key);
      if (check.length === 0) {
        newGrid?.terms.push({
          id: count,
          dataType: 'text',
          term: key,
          termType: 'field',
          displayName: key,
          name: key,
          metadata: {
            width: 100
          }
        });
        count++;
      }
    });
  });

  const cutIdIndex = newGrid?.terms?.find(t => t.term === GG_DEFAULTS.CUT_ID)?.id;

  if (!showPurchasedSurveys) {
    newGrid.data = [];
    let index = -1;

    forEach(surveyJobs, surveyJob => {
      if (!surveyJob.purchased) {
        index++;
        newGrid.data[index] = [];
        forEach(surveyJob, value => {
          newGrid.data[index].push(value);
        });
      }
    });
  } else {
    forEach(surveyJobs, surveyJob => {
      if (surveyJob.purchased) {
        forEach(newGrid.data, gridData => {
          if (gridData[cutIdIndex] === surveyJob.cutId) {
            forEach(surveyJob, value => {
              gridData.push(value);
            });
          }
        });
      }
    });
  }

  return newGrid;
}

MarketplaceMatchesGrid.propTypes = {
  filterText: string,
  groupId: string,
  grid: object,
  actions: object,
  showPurchasedSurveys: bool,
  benchmarkYear: number,
  minimumConfidence: number,
  currency: string,
  matches: object,
  masterAccountId: string,
  displayIn: string,
  selectedViewPk: number,
  addMatch: func,
  settings: object,
  jobId: oneOfType([number, string]),
  loadingId: number,
  deleteMatch: func,
  isMatchDeleting: bool,
  togglePopover: func,
  renderPopover: bool,
  selectedRowId: number,
  hideSelect: bool,
  onDataCutClick: func,
  setTotalFilteredPurchasedCount: func,
  setTotalFilteredUnpurchasedCount: func,
  addSelectedCutID: func,
  removeSelectedCutID: func,
  selectedSurveys: array,
  onSurveyJobTitleClick: func,
  updateModifiedDate: func,
  getSurveyJobDetailsPageHref: func,
  counts: object,
  onBack: func,
  isSlideout: bool,
  numericRounding: number,
  percentRounding: number
};

function MarketplaceMatchesGrid(props) {
  const [addedMatches, setAddedMatches] = useState([]);
  const dispatch = useDispatch();

  const surveyJobs = props.showPurchasedSurveys ? props.matches?.purchased : props.matches?.unpurchased;
  const options = {
    benchmarkYears: [props.benchmarkYear],
    displayIn: props.displayIn?.toLowerCase(),
    minimumConfidence: props.minimumConfidence,
    currency: props.currency,
    data: surveyJobs
      ?.filter(j => j.purchased)
      .map(j => {
        return {
          cutId: j.cutId
        };
      })
  };

  useEffect(() => {
    if (addedMatches.length > 0) {
      setAddedMatches([]);
    }
  }, [props.selectedSurveys]);

  // Reload grid data if jobs, view, or display-in are changed
  useEffect(() => {
    if (
      (props.matches?.purchased?.length > 0 || props.matches?.unpurchased?.length > 0) &&
      !props.matches?.loading &&
      !props.grid.loading &&
      props.showPurchasedSurveys
    ) {
      props.actions.reset();
      if (options.data?.length !== 0) {
        props.actions.search(`/cms/api/accounts/${props.masterAccountId}/survey-jobs`, options, 'post');
      }
    }
  }, [props.matches?.purchased, props.matches?.unpurchased, props.displayIn, props.selectedViewPk]);

  const newGrid = useMemo(() => {
    return combineData(props.matches, props.grid, props.showPurchasedSurveys);
  }, [props.matches, props.grid, props.showPurchasedSurveys]);

  const filteredData = filterPurchasedAndSearch(
    props.matches,
    props.showPurchasedSurveys,
    props.filterText,
    newGrid.data,
    newGrid.terms,
    props.settings
  );

  if (props.showPurchasedSurveys) {
    props.setTotalFilteredPurchasedCount(filteredData.length);
  } else {
    props.setTotalFilteredUnpurchasedCount(filteredData.length);
  }

  function areSettingsEmpty() {
    const { settings } = props;

    return (
      settings.geographies?.length === 0 &&
      settings.industries.length === 0 &&
      settings.minCompany == null &&
      settings.minEmployee == null &&
      settings.minMatchLikelihood === 30 &&
      settings.showNotPurchasedSurveys &&
      settings.showPurchasedSurveys
    );
  }

  // Determine if list has been filtered due to filter preferences
  const isListFiltered = !areSettingsEmpty();

  // Checks for no results after searching
  const noResultsAfterSearching =
    !props.matches?.loading && !props.grid?.loading && filteredData?.length === 0 && props.filterText !== '';

  const noResultsAfterFilter =
    !props.matches?.loading && !props.grid?.loading && filteredData?.length === 0 && isListFiltered;

  const noResultsWithoutFilters =
    !props.matches?.loading && !props.grid?.loading && filteredData?.length === 0 && !isListFiltered;

  // Show if no results shown after searching
  const noResultsAfterSearchingPlaceholder = (
    <div id="marketplace-matches__no-matches-container" className="marketplace-matches__no-results-container-search">
      <Placeholder
        className="pxl-text-small pxl-text-muted"
        primaryText={
          <span>
            No search results for <b>“ {props.filterText} ”</b>
          </span>
        }
        secondaryText="Please try different keywords"
        showText={true}
      />
    </div>
  );

  const noResultsAfterFilterPlaceholder = (
    <div id="marketplace-matches__no-matches-container" className="marketplace-matches__no-results-container-search">
      <Placeholder
        className="pxl-text-small pxl-text-muted"
        primaryText={<span>There are no marketplace matches based on applied filters</span>}
        secondaryText="Please adjust or clear flters to see matches"
        showText={true}
      />
    </div>
  );

  const noResultsWithoutFiltersPlaceholder = (
    <div id="marketplace-matches__no-matches-container" className="marketplace-matches__no-results-container-search">
      <Placeholder
        className="pxl-text-small pxl-text-muted"
        primaryText={
          <span>{`No ${props.showPurchasedSurveys ? 'active' : 'suggested'} marketplace matches found`}</span>
        }
        showText={true}
      />
    </div>
  );

  const showPlaceholder = noResultsAfterSearching || noResultsAfterFilter || noResultsWithoutFilters;
  let placeholderToShow;
  if (noResultsAfterSearching) {
    placeholderToShow = noResultsAfterSearchingPlaceholder;
  } else if (noResultsWithoutFilters) {
    placeholderToShow = noResultsWithoutFiltersPlaceholder;
  } else if (noResultsAfterFilter) {
    placeholderToShow = noResultsAfterFilterPlaceholder;
  }

  // Add custom onClick event to be used in genericGridRenderer
  function onClickLearnMore(rowData) {
    props.actions.showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_SURVEY_DETAILS, {
      onBack: props.onBack,
      isBigger: props.isSlideout,
      readOnly: props.hideSelect
    });
    const surveyJobs = props.showPurchasedSurveys ? props.matches?.purchased : props.matches?.unpurchased;
    props.actions.getSurveyDetails(rowData, surveyJobs, filteredData, '');
  }

  async function onClick(cutId) {
    if (isChecked(cutId)) {
      await props.deleteMatch(props.jobId, [cutId], null, props.benchmarkYear, false, null);
      props.removeSelectedCutID(cutId);

      const newAddedMatches = addedMatches;
      newAddedMatches.splice(newAddedMatches.indexOf(cutId), 1);

      setAddedMatches(newAddedMatches);
    } else {
      const surveyJobs = props.showPurchasedSurveys ? props.matches?.purchased : props.matches?.unpurchased;
      const rec = surveyJobs?.jobs?.find(job => job.cutId === cutId);
      let feedback;
      if (rec) {
        feedback = {
          recommendationsId: rec.recommendationsId,
          payscaleProduct: 'MarketPay',
          organizationJobTitle: rec.jobTitle,
          currency: rec.surveyCurrency,
          benchmarkYear: props.benchmarkYear,
          selectedMatches: [
            {
              surveyCode: rec.surveyCode,
              surveyJobCode: rec.surveyJobCode
            }
          ]
        };
      }

      await props.addMatch(props.jobId, cutId, feedback, false, props.benchmarkYear, null);
      props.addSelectedCutID(cutId);
      const newAddedMatches = addedMatches;
      newAddedMatches.push(cutId);

      setAddedMatches(newAddedMatches);
    }
    dispatch(jobMatchGridActions.setIsStale(props.jobId, true));
    props.updateModifiedDate(props.jobId, props.benchmarkYear);
  }

  function useSelectAccessor(row) {
    return row.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value;
  }

  function isRowLoading(row, loadingId) {
    return row.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value === loadingId;
  }

  function isChecked(cutId) {
    return props.selectedSurveys?.includes(cutId);
  }

  const { togglePopover, selectedRowId, renderPopover, counts, showPurchasedSurveys, filterText } = props;
  let noDataText = `No surveys found for your search entry "${filterText}"`;
  if (counts.purchased <= 0 && counts.unpurchased > 0 && showPurchasedSurveys) {
    noDataText = 'Your current pricing matches do not have any Marketplace Matches in your active library';
  } else if (counts.purchased > 0 && counts.unpurchased <= 0 && !showPurchasedSurveys) {
    noDataText =
      'Your current pricing matches do not have any suggested Marketplace Matches that are not already in your active library.';
  }

  if (props.counts.purchased > 0 && (props.grid?.loading || !props.grid?.loaded)) {
    return <InPlaceIndicator />;
  }

  return (
    <div
      className={classnames('generic-grid__container marketplace-matches__grid-container', {
        'unpurchased-grid-container': !props.showPurchasedSurveys
      })}
    >
      {showPlaceholder ? (
        placeholderToShow
      ) : (
        <>
          {isListFiltered && (
            <div className="filtered-indication-text">
              {`There ${
                filteredData.length === 1 ? 'is 1 marketplace match' : `are ${filteredData.length} marketplace matches`
              } based on applied filters`}
            </div>
          )}

          <AdvancedTable
            {...newGrid.tableProps}
            getDataRow={(row, idx) =>
              getGenericSurveyRow(row, idx, {
                onClick: onClickLearnMore,
                isRowLoading: isRowLoading(row, props.loadingId),
                isRowDeleting: props.isMatchDeleting,
                loadingText: 'Adding Match to Pricing...',
                deletingText: 'Removing Match from Pricing...',
                togglePopover,
                selectedRowId,
                renderPopover,
                addedMatches,
                onDataCutClick: props.onDataCutClick,
                onSurveyJobTitleClick: props.onSurveyJobTitleClick,
                getSurveyJobDetailsPageHref: props.getSurveyJobDetailsPageHref,
                isUnpurchased: !props.showPurchasedSurveys,
                numericRounding: props.numericRounding,
                percentRounding: props.percentRounding
              })
            }
            rowData={filteredData}
            totalCount={filteredData.length}
            columnHeaders={getTransformedColumns(
              newGrid.termGroups,
              newGrid.terms,
              filteredData,
              newGrid.defaultLeftColumns,
              newGrid.defaultRightColumns
            )}
            rowIndexPrefix={`marketplace-matches-${props.showPurchasedSurveys ? 'purchased' : 'unpurchased'}`}
            {...props.actions}
            stickyHeader
            useSelectAccessor={props.showPurchasedSurveys ? useSelectAccessor : null}
            isChecked={isChecked}
            onCheck={onClick}
            loadingId={props.loadingId}
            centerAlignHeaders
            dataLoading={props.loading}
            noDataText={noDataText}
            loadingText={'Loading Survey Job Data'}
            hideSelect={props.hideSelect}
          />
        </>
      )}
    </div>
  );
}

export default MarketplaceMatchesGrid;
